// @flow

import React from 'react';
import classnames from 'classnames';
import qs from 'querystring';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router';

import Slider from '../common/Slider';
import PlayableItemPreview from '../common/PlayableItemPreview';
import DiscoverLivestreamEventItem from './DiscoverLivestreamEventItem';

import styles from './DiscoverGroupSlider.css';
import stylesPlayableItemPreview from '../common/PlayableItemPreview.css';
import DiscoverLiveCompareItem from './DiscoverLiveCompareItem';
import type { DiscoverGroup } from '../../shapes/types';
import IconLabel from '../util/IconLabel';
import CapacitorRipple from '../capacitor/Ripple';
import {
  isInvertedGroup,
  isLiveCompareGroup,
  isLivestreamEventGroup,
  isPlayableGroup,
} from '../../selectors/discover';

declare var __CAPACITOR__: boolean;

type OwnProps = {
  group: DiscoverGroup,
  viewAllQueryParams?: Object,
  isHomePage?: boolean,
  onItemClick: Function,
  onItemBeforeTogglePlay: Function,
};

type Props = OwnProps;

function DiscoverGroupSlider({
  group,
  isHomePage,
  viewAllQueryParams,
  onItemClick,
  onItemBeforeTogglePlay,
}: Props) {
  const isLiveCompare = isLiveCompareGroup(group);
  const isLivestreamEvent = isLivestreamEventGroup(group);
  const isInverted = isInvertedGroup(group, isHomePage);
  const isPlayable = isPlayableGroup(group);

  const groupClassNames = classnames(styles.group, {
    [styles.inverted]: isInverted,
  });

  const getSliderItem = () => {
    if (isLivestreamEvent) {
      return DiscoverLivestreamEventItem;
    }

    if (isLiveCompare) {
      return DiscoverLiveCompareItem;
    }

    return PlayableItemPreview;
  };

  const getViewAllLink = () => {
    if (isInverted) {
      return '/live';
    }
    if (!group.canViewAll) {
      return null;
    }

    let to = '/discover';

    if (group.hasSubDiscoverPage && group.subDiscoverPage) {
      to += `/${group.subDiscoverPage}`;
    } else {
      to += `/${group.type}/${group.id}`;
      if (viewAllQueryParams) {
        to += '?' + qs.stringify(viewAllQueryParams);
      }
    }

    return to;
  };

  const renderItem = item => {
    const SliderItem = getSliderItem();

    return (
      <SliderItem
        item={item}
        key={item.content.id}
        onClick={onItemClick}
        beforeTogglePlay={onItemBeforeTogglePlay}
        isInverted={isInverted}
        isHomePage={isHomePage}
        isSliderItem
        groupId={group.id}
      />
    );
  };

  const viewAllLink = getViewAllLink();
  const viewAllLabel = (
    <FormattedMessage id="discover.group.show-more" defaultMessage="Show More" />
  );

  const ViewAllItem = (
    <Link
      to={viewAllLink}
      className={classnames(styles.viewAllItem, stylesPlayableItemPreview.bg)}
      onlyActiveOnIndex
    >
      {__CAPACITOR__ && (
        <div className={stylesPlayableItemPreview.ripple}>
          <CapacitorRipple />
        </div>
      )}
      <div className={styles.viewAllText}>
        {viewAllLabel}
        <IconLabel name="chevron-right" className={styles.viewAllIcon} />
      </div>
    </Link>
  );

  const sliderItems = group.items.map(renderItem);

  if (isPlayable && viewAllLink) {
    sliderItems.push(ViewAllItem);
  }

  return (
    <div className={groupClassNames} id={group.id}>
      <Slider
        title={group.name}
        subtitle={group.subtitle}
        items={sliderItems}
        viewAllLink={viewAllLink}
        viewAllLabel={viewAllLabel}
      />
    </div>
  );
}

// $FlowFixMe
export default React.memo(DiscoverGroupSlider);
